import type { LucideIcon } from "lucide-react";
import {
	Activity,
	AlertTriangle,
	ArrowDownRight,
	ArrowUpRight,
	BarChart3,
	Bell,
	BookOpen,
	Building2,
	CalendarCheck,
	CheckSquare,
	ClipboardList,
	Cog,
	Coins,
	CreditCard,
	Database,
	DollarSign,
	Factory,
	FileText,
	FolderTree,
	Globe,
	Landmark,
	Layers,
	LayoutDashboard,
	ListTodo,
	ListTree,
	Monitor,
	Package,
	Radio,
	RefreshCw,
	RotateCcw,
	Scale,
	Shield,
	ShoppingCart,
	Sparkles,
	TrendingUp,
	Truck,
	Users,
} from "lucide-react";

export interface ModuleNavItem {
	to: string;
	labelKey: string;
	icon: LucideIcon;
	feature?: string;
	exact?: boolean;
	/** Sub-group label key — items with the same group are visually grouped */
	group?: string;
}

export interface ModuleDef {
	id: string;
	labelKey: string;
	icon: LucideIcon;
	prefix: string;
	feature?: string;
	/** If true, sub-items are handled by the module's own menu bar, not the sidebar */
	hasMenuBar?: boolean;
	items: ModuleNavItem[];
}

export function getModuleDefs(): ModuleDef[] {
	return [
		{
			id: "accounting",
			labelKey: "nav:accounting",
			icon: BookOpen,
			prefix: "/accounting",
			feature: "accounting",
			hasMenuBar: true,
			items: [
				// ── Operations ──
				{
					to: "/accounting",
					labelKey: "accounting:nav_dashboard",
					icon: LayoutDashboard,
					exact: true,
					group: "accounting:nav_group_operations",
				},
				{
					to: "/accounting/journal",
					labelKey: "accounting:nav_journal",
					icon: BookOpen,
					group: "accounting:nav_group_operations",
				},
				{
					to: "/accounting/bank",
					labelKey: "accounting:nav_bank",
					icon: Landmark,
					group: "accounting:nav_group_operations",
				},
				// ── Master Data ──
				{
					to: "/accounting/accounts",
					labelKey: "accounting:nav_accounts",
					icon: ListTree,
					group: "accounting:nav_group_master",
				},
				{
					to: "/accounting/assets",
					labelKey: "accounting:nav_assets",
					icon: Building2,
					group: "accounting:nav_group_master",
				},
				{
					to: "/accounting/schemas",
					labelKey: "accounting:nav_schemas",
					icon: FileText,
					group: "accounting:nav_group_master",
				},
				// ── Reports ──
				{
					to: "/accounting/statements",
					labelKey: "accounting:nav_statements",
					icon: FileText,
					group: "accounting:nav_group_reports",
				},
				{
					to: "/accounting/receivables",
					labelKey: "accounting:nav_receivables",
					icon: ArrowDownRight,
					group: "accounting:nav_group_reports",
				},
				{
					to: "/accounting/payables",
					labelKey: "accounting:nav_payables",
					icon: ArrowUpRight,
					group: "accounting:nav_group_reports",
				},
				{
					to: "/accounting/budget",
					labelKey: "accounting:nav_budget",
					icon: Coins,
					group: "accounting:nav_group_reports",
				},
				{
					to: "/accounting/consolidation",
					labelKey: "accounting:nav_consolidation",
					icon: Layers,
					group: "accounting:nav_group_reports",
				},
				// ── Period ──
				{
					to: "/accounting/close",
					labelKey: "accounting:nav_close",
					icon: CalendarCheck,
					group: "accounting:nav_group_period",
				},
			],
		},
		{
			id: "catalog",
			labelKey: "nav:catalog",
			icon: Package,
			prefix: "/catalog",
			feature: "catalog",
			items: [
				{ to: "/catalog/products", labelKey: "nav:products", icon: Package },
				{
					to: "/catalog/categories",
					labelKey: "nav:categories",
					icon: FolderTree,
				},
			],
		},
		{
			id: "commerce",
			labelKey: "nav:commerce",
			icon: ShoppingCart,
			prefix: "/commerce",
			feature: "commerce",
			items: [
				{
					to: "/commerce",
					labelKey: "commerce:nav_dashboard",
					icon: LayoutDashboard,
					exact: true,
					group: "commerce:nav_group_operations",
				},
				{
					to: "/commerce/orders",
					labelKey: "commerce:nav_orders",
					icon: ShoppingCart,
					group: "commerce:nav_group_operations",
				},
				{
					to: "/commerce/quotes",
					labelKey: "commerce:nav_quotes",
					icon: FileText,
					group: "commerce:nav_group_operations",
				},
				{
					to: "/commerce/delivery",
					labelKey: "commerce:nav_delivery",
					icon: Truck,
					group: "commerce:nav_group_logistics",
				},
				{
					to: "/commerce/payments",
					labelKey: "commerce:nav_payments",
					icon: CreditCard,
					group: "commerce:nav_group_logistics",
				},
				{
					to: "/commerce/pricing",
					labelKey: "commerce:nav_pricing",
					icon: DollarSign,
					group: "commerce:nav_group_pricing",
				},
				{
					to: "/commerce/promotions",
					labelKey: "commerce:nav_promotions",
					icon: Sparkles,
					group: "commerce:nav_group_pricing",
				},
				{
					to: "/commerce/subscriptions",
					labelKey: "commerce:nav_subscriptions",
					icon: RefreshCw,
					group: "commerce:nav_group_operations",
				},
				{
					to: "/commerce/analytics",
					labelKey: "commerce:nav_analytics",
					icon: BarChart3,
					group: "commerce:nav_group_reports",
				},
				{
					to: "/commerce/returns",
					labelKey: "commerce:nav_returns",
					icon: RotateCcw,
					group: "commerce:nav_group_reports",
				},
				{
					to: "/commerce/shipments",
					labelKey: "commerce:nav_shipments",
					icon: Package,
					group: "commerce:nav_group_reports",
				},
			],
		},
		{
			id: "portals",
			labelKey: "nav:portals",
			icon: Globe,
			prefix: "/portals",
			feature: "portal",
			items: [],
		},
		{
			id: "advisor",
			labelKey: "nav:advisor",
			icon: Scale,
			prefix: "/advisor",
			feature: "advisor",
			items: [
				{ to: "/advisor", labelKey: "nav:advisor_engagements", icon: Scale },
			],
		},
		{
			id: "crm",
			labelKey: "nav:clients",
			icon: Users,
			prefix: "/parties",
			feature: "crm",
			items: [
				{ to: "/parties", labelKey: "nav:contacts", icon: Users },
				{ to: "/work-items", labelKey: "nav:tracker", icon: CheckSquare },
			],
		},
		{
			id: "monitoring",
			labelKey: "nav:monitoring",
			icon: Activity,
			prefix: "/activity",
			items: [
				{ to: "/activity", labelKey: "nav:activity", icon: Activity },
				{ to: "/notifications", labelKey: "nav:notifications", icon: Bell },
				{
					to: "/background-jobs",
					labelKey: "nav:background_jobs",
					icon: ListTodo,
				},
			],
		},
		{
			id: "diagnostics",
			labelKey: "nav:diagnostics",
			icon: Monitor,
			prefix: "/diagnostics",
			feature: "diagnostics",
			items: [
				{
					to: "/diagnostics",
					labelKey: "diagnostics:sidebar_dashboard",
					icon: Monitor,
					exact: true,
				},
				{
					to: "/diagnostics/sessions",
					labelKey: "diagnostics:sidebar_sessions",
					icon: Activity,
				},
				{
					to: "/diagnostics/devices",
					labelKey: "diagnostics:sidebar_devices",
					icon: Monitor,
				},
				{
					to: "/diagnostics/live",
					labelKey: "diagnostics:sidebar_live",
					icon: Radio,
				},
				{
					to: "/diagnostics/policies",
					labelKey: "diagnostics:sidebar_policies",
					icon: Shield,
				},
				{
					to: "/diagnostics/storage",
					labelKey: "diagnostics:sidebar_storage",
					icon: Database,
				},
				{
					to: "/diagnostics/analytics",
					labelKey: "diagnostics:sidebar_analytics",
					icon: BarChart3,
				},
			],
		},
		{
			id: "manufacturing",
			labelKey: "nav:manufacturing",
			icon: Factory,
			prefix: "/manufacturing",
			feature: "manufacturing",
			hasMenuBar: true,
			items: [
				{
					to: "/manufacturing",
					labelKey: "manufacturing:dashboard",
					icon: LayoutDashboard,
					exact: true,
				},
				{
					to: "/manufacturing/exceptions",
					labelKey: "manufacturing:exceptions",
					icon: AlertTriangle,
					group: "manufacturing:menu_planning",
				},
				{
					to: "/manufacturing/board",
					labelKey: "manufacturing:board",
					icon: ClipboardList,
					group: "manufacturing:menu_planning",
				},
				{
					to: "/manufacturing/actions",
					labelKey: "manufacturing:actions",
					icon: ListTodo,
					group: "manufacturing:menu_planning",
				},
				{
					to: "/manufacturing/production",
					labelKey: "manufacturing:production",
					icon: Cog,
					group: "manufacturing:menu_production",
				},
				{
					to: "/manufacturing/sop",
					labelKey: "manufacturing:sop",
					icon: TrendingUp,
					group: "manufacturing:menu_sop",
				},
			],
		},
	];
}

export function findActiveModule(pathname: string): ModuleDef | undefined {
	const defs = getModuleDefs();
	// Check if any workspace's items match the current path
	for (const ws of defs) {
		if (pathname.startsWith(ws.prefix)) return ws;
		if (ws.items.some((item) => pathname.startsWith(item.to))) return ws;
	}
	return undefined;
}
